import { useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import useModalTraits from '../../hooks/useModalTraits'
import Link from '../Link'
import Twitter from '../icons/Twitter'
import Facebook from '../icons/Facebook'
import LinkIcon from '../icons/Link'
import Email from '../icons/Email'

function ArticleSharingTools( { url, title } ) {
  const twitterUrl = `https://twitter.com/intent/tweet?text=${ title } — ${ encodeURI( url ) }`
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${ encodeURI( url ) }&t=${ title }`
  const emailUrl = `mailto:?subject=${ title } — Finance Uncovered&body=From Finance Uncovered…%0D%0A${ title }%0D%0A${ url }%0D%0A`
  
  return (
    <StyledSharingTools>
      <Link 
        href={ twitterUrl }
        target="_blank"
      >
        <Twitter color="black" />
      </Link>
      <Link 
        href={ fbUrl }
        target="_blank"
      >
        <Facebook color="black" />
      </Link>
      <LinkGrabber url={ url } />
      <Link 
        href={ emailUrl }
      >
        <Email color="black" />
      </Link>
    </StyledSharingTools>
  )
}

function LinkGrabber( { url } ) {
  const containerRef = useRef<HTMLDivElement>( null )
  const [isOpen, setIsOpen] = useState( false )
  const [isCopied, setIsCopied] = useState( false )

  const toggle = () => {
    setIsOpen( s => !s )
    setIsCopied( false )
  }

  const copyToClipboard = value => {
    if ( navigator?.clipboard ) {
      navigator.clipboard.writeText( value ).then( () => {
        setIsCopied( true )
      } )
    }
  }

  useModalTraits( {
    isOpen,
    close: () => {
      setIsCopied( false )
      setIsOpen( false )
    },
    containerRef,
    preventBodyScroll: false,
  } )

  return (
    <StyledLinkGrabber 
      $isOpen={ isOpen }
      $isCopied={ isCopied }
      ref={ containerRef }
    >
      <button
        type="button"
        onClick={ toggle }
      >
        <LinkIcon color="black" />
      </button>
      <div>
        <input
          value={ isCopied ? 'Copied!' : url }
        />
        <button
          type="button"
          onClick={ copyToClipboard }
        >
          Copy
        </button>
      </div>
    </StyledLinkGrabber>
  )
}

const buttonStyles = css`
  border: 0;
  background: transparent;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .3s ease-out;

  &:hover {
    background: ${ p => p.theme.colors.blueGrey__light };
  }
`

const StyledSharingTools = styled.div`
  position: relative;
  display: flex;

  > a {
    ${ buttonStyles }
  }
`

const StyledLinkGrabber = styled.div<{
  $isOpen: boolean,
  $isCopied: boolean,
}>`
  > button {
    ${ buttonStyles }
  }

  > div {
    position: absolute;
    overflow: hidden;
    left: 0;
    right: 0;
    display: flex;
    background: ${ p => p.theme.colors.blueGrey__superlight };
    opacity: 0;
    transform: translateX( -8px );
    pointer-events: none;
    transition: 
      opacity .3s ease-out,
      transform .5s ease-out;

    > input {
      flex-grow: 1;
      overflow: hidden;
      padding: 0.5rem;
      border: 1px solid ${ p => p.theme.colors.blueGrey__light };
      white-space: nowrap;
      ${ p => p.theme.typo.generalSans }
      font-size: ${ p => p.theme.typo.sizes.small };
    }

    > button {
      margin-left: auto;
      border: 0;
      background: ${ p => p.theme.colors.red };
      color: ${ p => p.theme.colors.white };
      ${ p => p.theme.typo.generalSans }
      font-size: ${ p => p.theme.typo.sizes.small };
      transition: background .3s ease-out;

      &:hover {
        background: ${ p => p.theme.colors.black };
      }
    }

    ${ p => p.$isOpen && css`
      opacity: 1;
      transform: translateX( 0 );
      pointer-events: all;
    ` }
  }
`

export default ArticleSharingTools
